import { computeRetirementTargets, computeStrategy, computeEffectiveSavings, accountBalance, savingsPlanMonthlyTarget } from './calculations';
import { ownedCategoryNames } from './categoryClass';

/**
 * Live Planned amount per envelope, using the same engine as the Strategy tab.
 *
 * Budget used to read whatever "Save all" last wrote. Toggling a debt plan on,
 * or parking surplus on NewRez, changed Strategy immediately and left Budget
 * on the old snapshot — which is how extra mortgage payoff vanished from
 * Planned. This is that snapshot, recomputed, so the two tabs can't disagree.
 *
 * Unlinked envelopes keep the amount stored on the category (fixed costs).
 */
export function computeEnvelopePlanned({
  categories = [],
  accounts = [],
  transactions = [],
  members = [],
  savingsPlans = [],
  debtPlans = [],
  spendingGoals = [],
  settings = null,
  incomes = [],
  fixedEdits = {},
} = {}) {
  if (!settings) settings = {};
  const byName = {};
  for (const c of categories) byName[c.name] = c.planned || 0;

  const linkedAccountBalance = (name) => {
    if (!name) return null;
    const a = accounts.find(x => x.name === name);
    if (!a) return null;
    return accountBalance(a, transactions);
  };
  const withLinkedBalances = (plans, field, useMagnitude) => plans.map(p => {
    const bal = linkedAccountBalance(p.linkedAccountName);
    return bal == null ? p : { ...p, [field]: useMagnitude ? Math.abs(bal) : bal };
  });

  const savingsResolved = withLinkedBalances(savingsPlans, 'balance', false);
  const debtsResolved   = withLinkedBalances(debtPlans, 'principal', true);

  const calcTargets = computeRetirementTargets({
    members, savingsPlans: savingsResolved, debtPlans: debtsResolved, settings, incomes,
  });

  const baseOf = (p) => savingsPlanMonthlyTarget(p, calcTargets);
  const debtBase  = (p) => (p.baseMonthlyPmt || 0) + (p.escrow || 0);
  const debtExtra = (p) => calcTargets.debtTargets[p.id]?.extraGoal || 0;
  const debtToggleAmt = (p) => p.toggleFullPayment ? debtBase(p) + debtExtra(p) : debtExtra(p);

  const ownedNames = ownedCategoryNames({
    spendingGoals, savingsPlans, debtPlans,
  });
  const getFixedPlanned = (cat) => (
    fixedEdits[cat.id] !== undefined ? fixedEdits[cat.id] : (cat.planned || 0)
  );
  const fixedCats = categories.filter(c => !ownedNames.has(c.name));
  const fixedDebtBase = debtPlans.filter(p => !p.toggleFullPayment)
    .reduce((s, p) => s + debtBase(p), 0);
  const fixedTotal = fixedCats.reduce((s, c) => s + (parseFloat(getFixedPlanned(c)) || 0), 0)
    + fixedDebtBase;

  const incomeByMemberId = {};
  for (const r of incomes) {
    if (r.memberId != null)
      incomeByMemberId[r.memberId] = (incomeByMemberId[r.memberId] || 0) + (parseFloat(r.amount) || 0);
  }
  const varBaseTotal = spendingGoals.reduce((s, g) => s + (parseFloat(g.monthlyAmount) || 0), 0);

  const { effSavingsByPlanId, effDebtExtraByPlanId, varScale } = computeEffectiveSavings({
    savingsPlans: savingsPlans.map(p => ({
      id: p.id, type: p.type, ownerMemberId: p.ownerMemberId,
      enabled: !!p.enabled, baseAmount: baseOf(p),
    })),
    debtPlans: debtPlans.map(p => ({
      id: p.id, enabled: !!p.enabled, extraGoal: debtToggleAmt(p),
    })),
    incomeByMemberId, varBaseTotal,
    max401kIncomePct: settings.max401kIncomePct ?? 0.80,
  });

  const strat = computeStrategy({
    incomes: incomes.map(r => ({ memberId: r.memberId, amount: parseFloat(r.amount) || 0 })),
    members,
    fixedTotal,
    spendingGoals:  spendingGoals.map(g => ({ id: g.id, amount: (parseFloat(g.monthlyAmount) || 0) * varScale })),
    savingsAmounts: savingsPlans.map(p => ({ id: p.id, amount: effSavingsByPlanId[p.id] || 0 })),
    debtExtras:     debtPlans.map(p => ({ id: p.id, amount: effDebtExtraByPlanId[p.id] || 0 })),
  });

  const sc = 1 - strat.savingsCut;
  const dc = 1 - strat.debtCut;
  const vc = 1 - strat.variableCut;

  const debtPlanned = (p) => p.toggleFullPayment
    ? (effDebtExtraByPlanId[p.id] || 0) * dc
    : debtBase(p) + (effDebtExtraByPlanId[p.id] || 0) * dc;

  for (const c of fixedCats) byName[c.name] = getFixedPlanned(c);
  for (const g of spendingGoals) {
    if (g.linkedCategoryName)
      byName[g.linkedCategoryName] = (parseFloat(g.monthlyAmount) || 0) * varScale * vc;
  }
  for (const p of savingsPlans) {
    if (p.linkedCategoryName)
      byName[p.linkedCategoryName] = (effSavingsByPlanId[p.id] || 0) * sc;
  }
  for (const p of debtPlans) {
    if (p.linkedCategoryName)
      byName[p.linkedCategoryName] = debtPlanned(p);
  }
  return byName;
}
